import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import {
  DSContextMenu,
  DSContextMenuButton,
  DSJobCard,
} from '@hundred5/design-system';
import React, { useCallback, useRef, useState } from 'react';

import { Icon } from '@/features/common';
import { DeleteConfirmationContextMenu } from '@/features/common/components/delete-confirmation-context-menu';
import { useClickOutside } from '@/features/common/hooks/use-click-outside';
import { REMOTE_LOCATION_OPTIONS } from '@/features/job-opening';
import { useWorkspaceId } from '@/hooks/router';
import { getJobopeningCareersQueryKey } from '@/queries/jobopenings/keys';
import { usePatchJobopeningDescriptionMutation } from '@/queries/jobopenings/mutations';
import { CareersOpeningOverview } from '@/queries/types';
import { queryClient } from '@/utils/reactQuery';
import {
  EMPLOYMENT_TYPE_LABELS,
  formatPublishedDate,
  WORKPLACE_TYPE_LABELS,
} from './careers-job-opening-list.utils';

interface CareersJobOpeningListItemProps {
  jobOpening: CareersOpeningOverview;
}

export const CareersJobOpeningListItem = ({
  jobOpening,
}: CareersJobOpeningListItemProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const mutation = usePatchJobopeningDescriptionMutation();
  const workspaceId = useWorkspaceId();
  const handleContextMenuClick = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);
  const contextMenuRef = useRef<null | HTMLDivElement>(null);
  const locale = navigator.language || 'en-US';

  const currencyFormatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: jobOpening.salaryCurrency || 'EUR', // We won't show EUR really, but it needs a default value
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    currencyDisplay: 'symbol',
  });
  useClickOutside(contextMenuRef, () => {
    setIsOpen(false);
  });

  const descriptionText = [
    jobOpening.workplaceType
      ? WORKPLACE_TYPE_LABELS[jobOpening.workplaceType]
      : false,
    (jobOpening.locationAreas || [])
      .map(
        (area) =>
          REMOTE_LOCATION_OPTIONS.find((option) => option.value === area)?.label
      )
      .join(', '),
    jobOpening.locationCity,
    jobOpening.department,
    jobOpening.employmentType
      ? EMPLOYMENT_TYPE_LABELS[jobOpening.employmentType]
      : false,
  ]
    .filter(Boolean)
    .join(' · ');

  const formatSalary = (min?: number, max?: number) => {
    if (!!min && !max) {
      return currencyFormatter.format(min);
    }
    if (!min && !!max) {
      return currencyFormatter.format(max);
    }
    if (!!min && !!max) {
      return `${currencyFormatter.format(min)} - ${currencyFormatter.format(max)}`;
    }
    return undefined;
  };

  return (
    <DSJobCard
      name={jobOpening.name}
      addedAt={formatPublishedDate(jobOpening.addedAt)}
      salary={formatSalary(jobOpening.salaryMin, jobOpening.salaryMax)}
      descriptionText={descriptionText}
      contextMenu={
        <div ref={contextMenuRef}>
          <DSContextMenuButton onClick={handleContextMenuClick} />
          <DSContextMenu open={isOpen} position={{ top: '-42px', right: '0' }}>
            <DSContextMenu.Item
              label="Remove job"
              icon={<Icon icon={regular('trash')} color="purple-60" />}
              dangerous={true}
              onClick={() => {
                setIsOpen(false);
                setIsDeleteConfirmOpen(true);
              }}
            />
          </DSContextMenu>
          <DeleteConfirmationContextMenu
            open={isDeleteConfirmOpen}
            title="Remove job from careers page?"
            message="Are you sure you want to remove your job from the Careers page?"
            deleteButtonText="Remove job"
            onClick={() => {
              mutation.mutate(
                {
                  workspaceId: Number(workspaceId),
                  openingId: Number(jobOpening.openingId),
                  data: {
                    addToCareersPage: false,
                  },
                },
                {
                  onSuccess: () => {
                    setIsDeleteConfirmOpen(false);
                    queryClient.invalidateQueries({
                      queryKey: getJobopeningCareersQueryKey(
                        Number(workspaceId)
                      ),
                    });
                  },
                }
              );
            }}
            onClose={() => setIsDeleteConfirmOpen(false)}
          />
        </div>
      }
    ></DSJobCard>
  );
};
